import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation } from '@apollo/react-hooks';
import Card from './Card';
import * as price from '../util/Price';
import Input, { SuffixedInput, MoneyInput, TextAreaInput } from './Input';
import {
  GET_CURRENT_ORGANIZATION,
  UPDATE_ORGANIZATION_CONFIGURATION,
  GET_REFERENCE
} from '../graphql/queries';
import Loader from './Loader';
import Button from './Button';

const Form = styled.form`
width: 60%;

@media (max-width:991px){
  width: 100%;
}
`;

const LoaderContainer = styled.div`
    position: absolute;
    top: calc(50vh - 60px);
    left: calc(50vw - 60px);
  `;

export const Image = styled.div`
  margin-top: 30px;
  background-image: ${(props) => `url(${props.src})`};
  background-size: cover;
  background-position: center;
  background-origin: unset;
  border-radius: ${(props) => props.radius || '5px'};
  height: 190px;
  width: 300px;
`;


export default function Configuration() {
  const [affiliateCredit, setAffiliateCredit] = useState('');
  const [firstTimeRecipientCredit, setFirstTimeRecipientCredit] = useState('');
  const [repeatRecipientCredit, setRepeatRecipientCredit] = useState('');
  const [returnLimit, setReturnLimit] = useState('');
  const [couponActivationExpiration, setCouponActivationExpiration] = useState('');
  const [couponRedemptionExpiration, setCouponRedemptionExpiration] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [reservationUrl, setReservationUrl] = useState('');
  const { data, loading } = useQuery(GET_CURRENT_ORGANIZATION);

  // Default invite message text
  const inviteLink =`${window.location.origin}/user/invite/?ref=XYZ123`
  const inviteLinkText = `Click here to activate your discount: ${inviteLink}`;
  const c = data.getCurrentOrganization?.configurations;
  const first = price.output(c?.firstTimeRecipientCredit, true);
  const defaultInviteMessage = `Hi, I had a great experience at ${data.getCurrentOrganization?.name} and I think you should check it out too. Redeem this discount for up to $${first} when you make a purchase at ${data.getCurrentOrganization?.name}, we'll both get a discount!`;
  
  const [inviteMessage, setInviteMessage] = useState(data.getCurrentOrganization?.configurations?.inviteMessage|| defaultInviteMessage)
 
  const [updateOrganizationConfiguration, { loading: updateLoading }] = useMutation(UPDATE_ORGANIZATION_CONFIGURATION, {
    variables: {
      configurations: {
        affiliateCredit: Number(price.convertToCents(affiliateCredit)),
        firstTimeRecipientCredit: Number(price.convertToCents(firstTimeRecipientCredit)),
        repeatRecipientCredit: Number(price.convertToCents(repeatRecipientCredit)),
        returnLimit: Number(returnLimit),
        couponActivationExpiration: Number(couponActivationExpiration),
        couponRedemptionExpiration: Number(couponRedemptionExpiration),
        websiteUrl,
        reservationUrl,
        welcomeMessage,
        inviteMessage,
      },
    },
    onError(error) {
      console.error(error);
    },
    onCompleted(data) {
      console.log(data);
    },
  });

  useEffect(() => {
    if (data && data.getCurrentOrganization) {
      const { getCurrentOrganization: { configurations } } = data;
      setAffiliateCredit(price.output(configurations.affiliateCredit, true));
      setFirstTimeRecipientCredit(price.output(configurations.firstTimeRecipientCredit, true));
      setRepeatRecipientCredit(price.output(configurations.repeatRecipientCredit, true));
      setReturnLimit(configurations.returnLimit);
      setCouponActivationExpiration(configurations.couponActivationExpiration);
      setCouponRedemptionExpiration(configurations.couponRedemptionExpiration);
      setWelcomeMessage(configurations.welcomeMessage);
      setWebsiteUrl(configurations.websiteUrl);
      setReservationUrl(configurations.reservationUrl);
    }
  }, [data]);

  if (loading) {
    return (
      <LoaderContainer>
        <Loader />
      </LoaderContainer>
    );
  }

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        updateOrganizationConfiguration();
      }}
    >
      <Card width="100%" padding="20px 30px">
        <MoneyInput
          top
          width="100%"
          type="text"
          header="CREDIT FOR REFERRER"
          value={affiliateCredit}
          onChange={(e) => {
            const amount = e.target.value;
            if (amount.match(/^\d{0,}(\.\d{0,2})?$/)) {
              setAffiliateCredit(amount);
            }
          }}
          required
        />
        <MoneyInput
          width="100%"
          type="text"
          header="DISCOUNT FOR NEW CUSTOMER"
          value={firstTimeRecipientCredit}
          onChange={(e) => {
            const amount = e.target.value;
            if (amount.match(/^\d{0,}(\.\d{0,2})?$/)) {
              setFirstTimeRecipientCredit(e.target.value);
            }
          }}
          required
        />
        <MoneyInput
          width="100%"
          type="text"
          header="DISCOUNT FOR REPEAT CUSTOMER"
          value={repeatRecipientCredit}
          onChange={(e) => {
            const amount = e.target.value;
            if (amount.match(/^\d{0,}(\.\d{0,2})?$/)) {
              setRepeatRecipientCredit(e.target.value);
            }
          }}
          required
        />
        {/* <SuffixedInput
          type="number"
          header="EXPIRATION TIME TO ACCEPT COUPON"
          value={couponActivationExpiration}
          onChange={(e) => {
            const amount = e.target.value;
            if (amount.match(/^[1-9]\d*$/)) {
              setCouponActivationExpiration(e.target.value);
            }
          }}
          suffix="DAYS"
          required
        /> */}
        <SuffixedInput
          width="100%"
          type="number"
          header="EXPIRATION TIME TO REDEEM DISCOUNT"
          value={couponRedemptionExpiration}
          onChange={(e) => {
            setCouponRedemptionExpiration(e.target.value);
          }}
          suffix="DAYS"
          required
        />
        <SuffixedInput
          width="100%"
          type="number"
          header="RETURN LIMIT"
          value={returnLimit}
          onChange={(e) => {
            setReturnLimit(e.target.value);
          }}
          suffix="DAYS"
          required
        />
        <Input
          width="100%"
          type="text"
          header="WEBSITE URL"
          value={websiteUrl}
          onChange={(e) => {
            setWebsiteUrl(e.target.value);
          }}
        />
        <Input
          width="100%"
          type="text"
          header="RESERVATION URL"
          value={reservationUrl}
          onChange={(e) => {
            setReservationUrl(e.target.value);
          }}
        />
        <TextAreaInput
        type="text"
        header="INVITE FRIENDS MESSAGE"
        value={inviteMessage}
        onChange={(e) => {
          setInviteMessage(e.target.value);
        }}
      />

      <TextAreaInput
        type="text"
        header="PREVIEW INVITE FRIEND MESSAGE"
        value={`${inviteMessage}\n\n${inviteLinkText}`}
      />
      </Card>
      <Button

        margin="40px 30px"
        text="UPDATE CONFIGURATION"
        type="submit"
        width="fit-content"
        loading={updateLoading}
      />
    </Form>
  );
}
